<script setup lang="ts">
import { Dark, setCssVar } from 'quasar';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MainLogo from '@/components/MainLogo.vue';

import { authState, logout } from '@/stores/auth';

const route = useRoute();
const router = useRouter();
const drawerOpen = ref(true);
const storedTheme = localStorage.getItem('admin_theme');
const prefersDarkMedia = window.matchMedia?.('(prefers-color-scheme: dark)') ?? null;
const systemPrefersDark = ref(prefersDarkMedia?.matches ?? false);
const darkMode = ref(storedTheme === 'dark');
const searchTerm = ref('');
const searchLoading = ref(false);
const searchResults = ref<SearchResult[]>([]);
const searchFocused = ref(false);
const searchMenuOpen = ref(false);
const activeSearchIndex = ref(-1);
let searchAbortController: AbortController | null = null;
let searchRequestId = 0;
let searchBlurTimer: number | undefined;

interface SearchResult {
  id: string;
  type: 'project' | 'error' | 'integration' | 'documentation' | 'user' | 'report' | 'plugin';
  title: string;
  subtitle: string;
  icon: string;
  to: string;
}

function toDateTimeLocal(date: Date) {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
}

const defaultEndDate = new Date();
const defaultStartDate = new Date(defaultEndDate.getTime() - 7 * 24 * 60 * 60 * 1000);
const dashboardStart = ref(localStorage.getItem('project_dashboard_start') ?? toDateTimeLocal(defaultStartDate));
const dashboardEnd = ref(localStorage.getItem('project_dashboard_end') ?? toDateTimeLocal(defaultEndDate));

function applyThemeColors(isDark: boolean) {
  setCssVar('primary', isDark ? '#34D399' : '#4F46E5');
  setCssVar('secondary', isDark ? '#F9FAFB' : '#111827');
}

function setLinkHref(id: string, href: string) {
  const element = document.getElementById(id);
  if (element instanceof HTMLLinkElement) {
    element.href = href;
  }
}

function applyFavicons(isDark: boolean) {
  const theme = isDark ? 'dark' : 'light';
  setLinkHref('apple-touch-icon', `/favicons/${theme}/apple-touch-icon.png`);
  setLinkHref('favicon-32', `/favicons/${theme}/favicon-32x32.png`);
  setLinkHref('favicon-16', `/favicons/${theme}/favicon-16x16.png`);
  setLinkHref('shortcut-icon', `/favicons/${theme}/favicon.ico`);
  setLinkHref('web-manifest', `/favicons/${theme}/manifest.json`);
}

function setThemePreference(isDark: boolean) {
  darkMode.value = isDark;
  Dark.set(isDark);
  applyThemeColors(isDark);
  applyFavicons(isDark);
  localStorage.setItem('admin_theme', isDark ? 'dark' : 'light');
}

Dark.set(darkMode.value);
applyThemeColors(darkMode.value);
applyFavicons(darkMode.value);

const pageTitle = computed(() => {
  const titles: Record<string, string> = {
    dashboard: 'Dashboard',
    login: 'Login',
    'invitation-accept': 'Einladung',
    'project-error-detail': 'Error',
    'project-detail': 'Projekt',
    projects: 'Projekte',
    plugins: 'Plugins',
    users: 'Benutzer',
    reports: 'Berichte',
    'user-settings': 'User Settings',
    'api-documentation': 'API Dokumentation',
    'sdk-documentation': 'SDK Dokumentation',
    'documentation-templates': 'Vorlagen',
    'concept-documentation': 'Konzept Dokumentation',
    'legal-notice': 'Impressum',
    'privacy-policy': 'Datenschutz',
  };

  return titles[String(route.name)] ?? 'Bug I Am';
});

const isAdmin = computed(() => authState.user?.role === 'admin');
const isLockedUser = computed(() => Boolean(authState.user?.isLocked));
const isAuthenticated = computed(() => Boolean(authState.user));
const isPublicLegalRoute = computed(() => route.name === 'legal-notice' || route.name === 'privacy-policy');
const isPlainShellRoute = computed(() => Boolean(route.meta.plainShell) || (!isAuthenticated.value && isPublicLegalRoute.value));
const themeSwitchLabel = computed(() =>
  `Dark Mode ${darkMode.value ? 'aktiv' : 'inaktiv'} · System: ${systemPrefersDark.value ? 'Dark' : 'Light'}`,
);
const links = computed(() =>
  [
  { label: 'Dashboard', icon: 'dashboard', to: '/' },
  { label: 'Projekte', icon: 'folder_open', to: '/projects' },
  { label: 'Plugins', icon: 'extension', to: '/plugins' },
    isAdmin.value ? { label: 'Benutzer', icon: 'group', to: '/users' } : null,
    isAdmin.value ? { label: 'Berichte', icon: 'insights', to: '/reports' } : null,
  { label: 'User Settings', icon: 'manage_accounts', to: '/settings' },
  ].filter((link): link is { label: string; icon: string; to: string } => Boolean(link)),
);

const documentationLinks = computed(() =>
  [
    { label: 'API Dokumentation', icon: 'api', to: '/documentation/api' },
    { label: 'SDK Dokumentation', icon: 'integration_instructions', to: '/documentation/sdks' },
    { label: 'Vorlagen', icon: 'content_copy', to: '/documentation/templates' },
    isAdmin.value ? { label: 'Konzept Dokumentation', icon: 'architecture', to: '/documentation/concept' } : null,
  ].filter((link): link is { label: string; icon: string; to: string } => Boolean(link)),
);
const userInitials = computed(() =>
  authState.user?.name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'BI',
);
async function handleLogout() {
  await logout();
  await router.push({ name: 'login' });
}

function handleSystemThemeChange(event: MediaQueryListEvent) {
  systemPrefersDark.value = event.matches;
}

function updateDashboardRange() {
  localStorage.setItem('project_dashboard_start', dashboardStart.value);
  localStorage.setItem('project_dashboard_end', dashboardEnd.value);
  window.dispatchEvent(
    new CustomEvent('project-dashboard-range-change', {
      detail: {
        start: dashboardStart.value,
        end: dashboardEnd.value,
      },
    }),
  );
}

async function loadSearchResults(query: string) {
  searchAbortController?.abort();

  const normalizedQuery = query.trim();
  if (normalizedQuery.length < 3) {
    searchRequestId += 1;
    searchResults.value = [];
    searchLoading.value = false;
    searchMenuOpen.value = false;
    activeSearchIndex.value = -1;
    return;
  }

  searchAbortController = new AbortController();
  const requestId = ++searchRequestId;
  searchLoading.value = true;

  try {
    const response = await fetch(`/api/search?q=${encodeURIComponent(normalizedQuery)}`, {
      signal: searchAbortController.signal,
    });

    if (!response.ok) {
      if (requestId === searchRequestId) {
        searchResults.value = [];
      }
      return;
    }

    const data = (await response.json()) as { results: SearchResult[] };
    if (requestId === searchRequestId) {
      searchResults.value = data.results;
      activeSearchIndex.value = data.results.length ? 0 : -1;
    }
  } catch (error) {
    if (requestId === searchRequestId && !(error instanceof DOMException && error.name === 'AbortError')) {
      searchResults.value = [];
    }
  } finally {
    if (requestId === searchRequestId) {
      searchLoading.value = false;
    }
  }
}

async function openSearchResult(result: SearchResult) {
  searchTerm.value = '';
  searchResults.value = [];
  searchFocused.value = false;
  searchMenuOpen.value = false;
  activeSearchIndex.value = -1;
  await router.push(result.to);
}

function handleSearchBlur() {
  searchBlurTimer = window.setTimeout(() => {
    searchFocused.value = false;
    searchMenuOpen.value = false;
  }, 160);
}

function handleSearchFocus() {
  if (searchBlurTimer) {
    window.clearTimeout(searchBlurTimer);
  }
  searchFocused.value = true;
  searchMenuOpen.value = searchTerm.value.trim().length >= 3;
}

function selectSearchResult(index: number) {
  const result = searchResults.value[index];
  if (result) {
    void openSearchResult(result);
  }
}

function moveSearchSelection(direction: 1 | -1) {
  if (!searchResults.value.length) return;

  const nextIndex = activeSearchIndex.value + direction;
  if (nextIndex < 0) {
    activeSearchIndex.value = searchResults.value.length - 1;
    return;
  }

  if (nextIndex >= searchResults.value.length) {
    activeSearchIndex.value = 0;
    return;
  }

  activeSearchIndex.value = nextIndex;
}

function handleSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    searchMenuOpen.value = false;
    activeSearchIndex.value = -1;
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    searchMenuOpen.value = searchTerm.value.trim().length >= 3;
    moveSearchSelection(1);
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    searchMenuOpen.value = searchTerm.value.trim().length >= 3;
    moveSearchSelection(-1);
    return;
  }

  if (event.key === 'Enter' && searchMenuOpen.value && activeSearchIndex.value >= 0) {
    event.preventDefault();
    selectSearchResult(activeSearchIndex.value);
  }
}

watch(searchTerm, (value) => {
  searchMenuOpen.value = searchFocused.value && value.trim().length >= 3;
  activeSearchIndex.value = -1;
  void loadSearchResults(value);
});

onMounted(() => {
  prefersDarkMedia?.addEventListener('change', handleSystemThemeChange);
});

onUnmounted(() => {
  prefersDarkMedia?.removeEventListener('change', handleSystemThemeChange);
});
</script>

<template>
  <q-layout v-if="isPlainShellRoute" view="hHh lpr fFf" class="admin-shell">
    <q-header v-if="!isAuthenticated && isPublicLegalRoute" elevated class="legal-header">
      <q-toolbar class="legal-toolbar">
        <MainLogo class="header-logo" />
        <q-toolbar-title>{{ pageTitle }}</q-toolbar-title>
        <q-btn unelevated color="primary" icon="login" label="Login" to="/login" />
      </q-toolbar>
    </q-header>

    <q-page-container>
      <router-view />
    </q-page-container>
    <q-footer bordered class="legal-footer">
      <div class="legal-footer__inner">
        <router-link to="/impressum">Impressum</router-link>
        <router-link to="/datenschutz">Datenschutz</router-link>
      </div>
    </q-footer>
  </q-layout>

  <q-layout v-else view="hHh Lpr lff" class="admin-shell">
    <q-header elevated class="admin-header">
      <q-toolbar class="admin-toolbar">
        <q-btn v-if="!isLockedUser" flat dense round icon="menu" aria-label="Navigation" @click="drawerOpen = !drawerOpen" />
        <router-link v-if="isAuthenticated" to="/" aria-label="Zum Dashboard" class="header-logo-link">
          <MainLogo class="header-logo" />
        </router-link>
        <MainLogo v-else class="header-logo" />
        <q-toolbar-title>{{ isLockedUser ? 'Account gesperrt' : pageTitle }}</q-toolbar-title>
        <q-input
          v-if="!isLockedUser && isAuthenticated"
          v-model="searchTerm"
          dense
          borderless
          debounce="250"
          class="admin-search"
          placeholder="Suche... (Projekte, Errors, API-Keys, Integrationen, Doku)"
          @focus="handleSearchFocus"
          @blur="handleSearchBlur"
          @keydown="handleSearchKeydown"
        >
          <template #prepend>
            <q-icon name="search" />
          </template>
          <template #append>
            <q-spinner v-if="searchLoading" size="18px" />
          </template>
          <q-menu
            v-model="searchMenuOpen"
            no-parent-event
            no-focus
            no-refocus
            fit
            anchor="bottom left"
            self="top left"
            class="admin-search-menu"
          >
            <q-list separator>
              <q-item v-if="!searchResults.length && !searchLoading">
                <q-item-section>
                  <q-item-label>Keine Treffer</q-item-label>
                  <q-item-label caption>Suche nach Projekten, Errors, API-Keys, Integrationen oder Doku.</q-item-label>
                </q-item-section>
              </q-item>
              <q-item
                v-for="(result, index) in searchResults"
                :key="result.id"
                v-ripple
                clickable
                :active="index === activeSearchIndex"
                active-class="admin-search-result--active"
                @click="openSearchResult(result)"
                @mouseenter="activeSearchIndex = index"
              >
                <q-item-section avatar>
                  <q-icon :name="result.icon" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ result.title }}</q-item-label>
                  <q-item-label caption>{{ result.subtitle }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-badge outline color="primary">{{ result.type }}</q-badge>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-input>
        <div class="toolbar-actions">
          <span v-if="authState.user" class="header-user">{{ authState.user.name }}</span>
          <q-toggle
            :model-value="darkMode"
            checked-icon="dark_mode"
            unchecked-icon="light_mode"
            color="primary"
            keep-color
            :aria-label="themeSwitchLabel"
            :title="themeSwitchLabel"
            @update:model-value="setThemePreference"
          />
          <q-btn
            v-if="isAuthenticated"
            flat
            round
            icon="logout"
            aria-label="Logout"
            @click="handleLogout"
          />
          <q-btn
            v-else
            flat
            icon="login"
            label="Login"
            to="/login"
          />
        </div>
      </q-toolbar>
    </q-header>

    <q-drawer v-if="!isLockedUser" v-model="drawerOpen" show-if-above :width="280" class="admin-drawer">
      <div v-if="authState.user" class="drawer-profile">
        <q-avatar size="52px" class="admin-avatar">
          {{ userInitials }}
        </q-avatar>
        <div>
          <strong>{{ authState.user.name }}</strong>
          <span>{{ authState.user.email }}</span>
        </div>
      </div>

      <p class="nav-caption">Main</p>

      <q-list padding class="nav-list">
        <q-item
          v-for="link in links"
          :key="link.to"
          v-ripple
          clickable
          :to="link.to"
          exact
          active-class="nav-item--active"
          class="nav-item"
        >
          <q-item-section avatar>
            <q-icon :name="link.icon" />
          </q-item-section>
          <q-item-section>{{ link.label }}</q-item-section>
        </q-item>
      </q-list>

      <p class="nav-caption">Knowledge</p>

      <q-list padding class="nav-list nav-list--nested">
        <q-expansion-item
          expand-separator
          icon="article"
          label="Dokumentation"
          class="nav-expansion"
          :default-opened="String(route.name).includes('documentation')"
        >
          <q-item
            v-for="link in documentationLinks"
            :key="link.to"
            v-ripple
            clickable
            :to="link.to"
            exact
            active-class="nav-item--active"
            class="nav-item nav-item--child"
          >
            <q-item-section avatar>
              <q-icon :name="link.icon" />
            </q-item-section>
            <q-item-section>{{ link.label }}</q-item-section>
          </q-item>
        </q-expansion-item>
      </q-list>

      <div class="drawer-footer">
        <p>Dashboard Zeitraum</p>
        <q-input
          v-model="dashboardStart"
          dense
          outlined
          type="datetime-local"
          label="Start"
          @update:model-value="updateDashboardRange"
        />
        <q-input
          v-model="dashboardEnd"
          dense
          outlined
          type="datetime-local"
          label="Ende"
          @update:model-value="updateDashboardRange"
        />
        <div class="drawer-legal-links">
          <router-link to="/impressum">Impressum</router-link>
          <router-link to="/datenschutz">Datenschutz</router-link>
        </div>
      </div>
    </q-drawer>

    <q-page-container>
      <q-page v-if="isLockedUser" class="page-surface locked-page">
        <q-card flat bordered class="panel-card locked-card">
          <q-card-section>
            <div class="section-heading">
              <div>
                <p class="eyebrow">Account gesperrt</p>
                <h1>Dein Benutzer wurde gesperrt.</h1>
              </div>
              <q-icon name="lock" color="negative" size="42px" />
            </div>
            <p class="muted-copy">
              Du bist angemeldet, kannst aber aktuell keine Projekte, Berichte, Einstellungen oder API-Daten verwenden.
              Bitte wende dich an einen Administrator.
            </p>
            <q-btn unelevated color="primary" icon="logout" label="Logout" @click="handleLogout" />
          </q-card-section>
        </q-card>
      </q-page>
      <router-view v-else />
    </q-page-container>
  </q-layout>
</template>

<style scoped>
.header-logo {
  height: 40px;
  width: auto;
  flex: 0 0 auto;
}

.header-logo-link {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  color: inherit;
  text-decoration: none;
}

.legal-footer {
  background: var(--surface-card);
  border-top: 1px solid var(--border-soft);
}

.legal-header {
  background: var(--surface-card);
  color: var(--text-strong);
  border-bottom: 1px solid var(--border-soft);
}

.legal-toolbar {
  min-height: 72px;
  padding: 0 24px;
}

.legal-footer__inner,
.drawer-legal-links {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.legal-footer__inner {
  justify-content: center;
  padding: 14px 18px;
}

.legal-footer__inner a,
.drawer-legal-links a {
  color: var(--text-muted);
  text-decoration: none;
  font-weight: 600;
}

.legal-footer__inner a:hover,
.drawer-legal-links a:hover {
  color: var(--brand-primary);
}

.drawer-legal-links {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--border-soft);
  justify-content: center;
  text-align: center;
}

.drawer-profile {
  margin-top: 1rem;
}
</style>
